import { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { useSession } from './SessionContext';
import { createSupabaseWithAuth } from './SupabaseConfig';

/**
 * Custom hook for skills autocomplete with debounced search
 * 
 * Provides skills autocomplete functionality with:
 * - Debounced search (300ms)
 * - Loading state management
 * - Automatic cleanup
 * - Error handling
 * 
 * @returns {Object} {
 *   skillsOptions: Array of {value, label} options for ComboBoxSelect
 *   isLoadingSkills: Boolean loading state
 *   searchSkills: Debounced search function to call with search term
 *   clearSkills: Function to clear the options list
 * }
 * 
 * @example
 * const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();
 * 
 * // In ComboBoxSelect onInputChange:
 * onInputChange={(inputValue, action) => {
 *   if (action.action === 'input-change') {
 *     if (inputValue && inputValue.length >= 2) {
 *       searchSkills(inputValue);
 *     } else {
 *       clearSkills();
 *     }
 *   }
 * }}
 */
const useSkillsAutocomplete = () => {
  const { user } = useSession();
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);

  // Fetch skills from Supabase
  const fetchSkills = useCallback(async (searchTerm = '') => {
    if (!user || !user.access_token) {
      console.error('User session not available');
      return;
    }

    setIsLoadingSkills(true);
    try {
      const supabaseWithAuth = createSupabaseWithAuth(user.access_token);
      
      let query = supabaseWithAuth
        .from('skills')
        .select('skill_name')
        .limit(20);

      if (searchTerm && searchTerm.length >= 2) {
        query = query.ilike('skill_name', `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching skills:', error);
        return;
      }

      const formattedSkills = data.map(skill => ({
        value: skill.skill_name,
        label: skill.skill_name
      }));

      setSkillsOptions(formattedSkills);
    } catch (error) {
      console.error('Error fetching skills:', error);
    } finally {
      setIsLoadingSkills(false);
    }
  }, [user]);

  // Debounced search function
  const debouncedFetchSkills = useMemo(
    () => debounce(fetchSkills, 300),
    [fetchSkills]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFetchSkills.cancel();
    };
  }, [debouncedFetchSkills]);

  // Clear options function
  const clearSkills = useCallback(() => {
    setSkillsOptions([]);
  }, []);

  return {
    skillsOptions,
    isLoadingSkills,
    searchSkills: debouncedFetchSkills,
    clearSkills
  };
};

export default useSkillsAutocomplete;
